import { GraphBlock } from '@/blocks/graph/graph-block'
import { HighlightBlockExtended } from '@/blocks/highlight-block/highlight-block-extended'
import { ImageBlock } from '@/blocks/image-block/image-block'
import { LinkCollectionBlock } from '@/blocks/link-collection-block/link-collection-block'
import { ListContainerBlock } from '@/blocks/list-container/list-container-block'
import { ManualPageListBlock } from '@/blocks/manual-page-list-block/manual-page-list-block'
import { PowerBIBlock } from '@/blocks/power-bi/power-bi-block'
import { SearchArticleListBlock } from '@/blocks/search-article-list/search-article-list-block'
import { SearchLinkListBlock } from '@/blocks/search-link-list/search-link-list-block'
import '@/pages/statistic/statisticspage-content-layout.scss'
import { AnchorNav } from '@platformui/core/components/anchor-navigation/anchor-navigation'
import { ArticleHeader } from '@platformui/core/components/article-header/article-header'
import { BreadcrumbsPlatformUI } from '@platformui/core/components/breadcrumbs/breadcrumbs'
import { LocalNavigation } from '@platformui/core/components/local-navigation/local-navigation'
import { usePageInfo } from '@platformui/core/infrastructure/features/page-info/page-info-hook'
import { BlockRendererProvider } from '@platformui/core/infrastructure/features/renderer/block-renderer-context'
import { ContentAreaRenderer } from '@platformui/core/infrastructure/features/renderer/content-area-renderer'
import {
    LayoutWidth,
    PageContentSection,
} from '@platformui/core/infrastructure/features/renderer/layout/page-content-layout'
import {
    GridAlign,
    PageGridLayout,
} from '@platformui/core/infrastructure/features/renderer/layout/page-grid-layout'

const blocksAvailableOnStatisticPage = {
    ImageBlock,
    LinkCollectionBlock,
    ManualPageListBlock,
    ListContainerBlock,
    HighlightBlockExtended,
    SearchLinkListBlock,
    PowerBIBlock,
    GraphBlock,
    SearchArticleListBlock,
}

export const StatisticsPage = () => {
    const { content, breadcrumbs, localMenu, translations, pageInfo } =
        usePageInfo()

    const {
        heading,
        preamble,
        mainContent,
        sectionContent,
        showLocalNav = false,
        anchorNavigation = [],
        hideAnchorNavigation,
        headingTranslation,
    } = content

    const { dir, language } = pageInfo
    const shouldUseTwoColumnLayout =
        (showLocalNav && localMenu) ||
        (!hideAnchorNavigation && anchorNavigation.length > 0)
    return (
        <BlockRendererProvider availableBlocks={blocksAvailableOnStatisticPage}>
            <PageContentSection width={LayoutWidth.Full}>
                <PageContentSection
                    width={LayoutWidth.Wide}
                    center={true}
                    padding={{
                        default: { left: '1.25rem', right: '1.25rem' },
                    }}
                >
                    <BreadcrumbsPlatformUI
                        breadcrumbs={breadcrumbs?.breadcrumbs}
                        language={language}
                        label={breadcrumbs?.label}
                        name={breadcrumbs?.name}
                    />
                </PageContentSection>

                <PageContentSection width={LayoutWidth.Wide} center={true}>
                    <PageGridLayout
                        flow={{
                            default: GridAlign.Flow.Row,
                            desktop: GridAlign.Flow.Column,
                        }}
                        gap={{ desktop: '2.5rem' }}
                        justifyContent={{
                            desktop: GridAlign.Content.SpaceBetween,
                        }}
                        fillGrid={{
                            default: true,
                            desktop: true,
                        }}
                    >
                        <main className="main">
                            <PageGridLayout
                                flow={GridAlign.Flow.Row}
                                id="content"
                                dir={dir}
                                gap={{ default: '2rem', desktop: '2.5rem' }}
                            >
                                <PageContentSection width={LayoutWidth.Wide}>
                                    <ArticleHeader
                                        modifiers={{
                                            'c-articleheader--article-page': true,
                                        }}
                                        heading={heading}
                                        preamble={preamble}
                                        headingTranslation={headingTranslation}
                                        subjectAboveHeading={true}
                                    />
                                </PageContentSection>

                                <PageGridLayout
                                    flow={GridAlign.Flow.Row}
                                    gap={{ default: '2rem', desktop: '2.5rem' }}
                                >
                                    <ContentAreaRenderer
                                        blocks={mainContent}
                                        propertyName="MainContent"
                                        blockProps={{ isArticlePage: true }}
                                    />
                                </PageGridLayout>
                                {sectionContent?.length > 0 && (
                                    <PageContentSection
                                        width={LayoutWidth.Wide}
                                    >
                                        <PageGridLayout
                                            flow={GridAlign.Flow.Row}
                                            gap={{
                                                default: '2rem',
                                                desktop: '2.5rem',
                                            }}
                                        >
                                            <ContentAreaRenderer
                                                blocks={sectionContent}
                                                propertyName="SectionContent"
                                            />
                                        </PageGridLayout>
                                    </PageContentSection>
                                )}
                            </PageGridLayout>
                        </main>
                        {shouldUseTwoColumnLayout && (
                            <PageContentSection
                                noPadding
                                width={LayoutWidth.Narrow}
                            >
                                <PageGridLayout
                                    gap={{
                                        desktop: '2.5rem',
                                    }}
                                    flow={GridAlign.Flow.Row}
                                    alignContent={{
                                        desktop: GridAlign.Content.Start,
                                    }}
                                >
                                    {showLocalNav && localMenu && (
                                        <LocalNavigation
                                            localMenu={localMenu}
                                        />
                                    )}
                                    {!hideAnchorNavigation && (
                                        <AnchorNav
                                            links={anchorNavigation}
                                            anchorHeading={
                                                translations?.anchorheading
                                            }
                                        />
                                    )}
                                </PageGridLayout>
                            </PageContentSection>
                        )}
                    </PageGridLayout>
                </PageContentSection>
            </PageContentSection>
        </BlockRendererProvider>
    )
}
